import React, { useState } from 'react'
import { Button, Card, Grid, IconButton, Stack, Typography, useTheme } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CloseIcon from '@mui/icons-material/Close'
import { useCurrentPractice } from 'contexts/PracticeContext'
import { useCapitalAccess } from 'hooks/useCapitalAccess'
import useStripeCapitalConnect from 'hooks/useStripeCapitalConnect'
import { useCapitalApplicationSubmitted } from 'util/capitalSubmitted'
import { dismissCapitalGuide, useCapitalGuideDismissed } from 'util/capitalGuideDismissed'
import { trackCapitalInteraction } from 'apis/consolidated-practice'
import { PdfViewerModal } from 'components/common/PdfViewerModal'
import { CAPITAL_GUIDES_CONFIG } from 'config'

const GUIDE_TITLE = 'How Will You Use Capital?'

/**
 * Sidebar "free guide" promo card, rendered directly above `CapitalPromoTile`
 * in the dashboard sidebar's bottom section. Surfaces the compliance-approved
 * "How Will You Use Capital?" one-pager so eligible practices can read it
 * in-app via `PdfViewerModal` instead of leaving the dashboard.
 *
 * Shares its visibility gate with `CapitalPromoTile` exactly: not just
 * `useCapitalAccess` (Capital-enabled practice + hospital manager/admin
 * role), but also a live offer the practice can still act on (backend
 * `promo`, non-null `offerAmount`, application not yet submitted) —
 * otherwise a rejected/expired/no-offer practice would see "read about
 * Capital" copy with nothing to actually apply for. Also hidden when the
 * guide PDF URL isn't configured for the environment, so "Read Now" can
 * never open an empty viewer.
 *
 * The illustration block is a placeholder gradient + icon standing in for a
 * real creative asset that hasn't been produced yet; swap it out once art is
 * available.
 */
const CapitalGuideBanner: React.FC = () => {
	const theme = useTheme()
	const { currentPracticeObject } = useCurrentPractice()
	const { canAccess, isGuideHidden } = useCapitalAccess()
	const [isModalOpen, setIsModalOpen] = useState(false)

	const consolidatedPracticeId = currentPracticeObject?.consolidatedPractice?.id
	const { offerId, offerAmount, isPromoOffer } = useStripeCapitalConnect(
		canAccess ? consolidatedPracticeId : undefined
	)
	const applicationSubmitted = useCapitalApplicationSubmitted(consolidatedPracticeId)
	const dismissed = useCapitalGuideDismissed(consolidatedPracticeId)

	const pdfUrl = CAPITAL_GUIDES_CONFIG.howWillYouUseCapitalPdfUrl

	const willRender =
		canAccess &&
		isPromoOffer &&
		offerAmount !== null &&
		!applicationSubmitted &&
		Boolean(pdfUrl) &&
		!dismissed &&
		!isGuideHidden

	if (!willRender) {
		return null
	}

	const handleOpen = (): void => {
		setIsModalOpen(true)
		if (consolidatedPracticeId) {
			trackCapitalInteraction(consolidatedPracticeId, 'how_will_you_use_capital_guide_opened', {
				offerId,
				offerAmount,
				openedFrom: 'sidebar'
			})
		}
	}

	// Owns its own `Grid` row so the sidebar can render it unconditionally: an
	// outer wrapper there would still lay out a spacing-padded, empty row for
	// every practice that isn't eligible.
	return (
		<Grid size={12}>
			<Card
				elevation={0}
				sx={{
					background: `linear-gradient(135deg, ${theme.palette.capital.primary} 0%, ${theme.palette.capital.accentDeep} 100%)`,
					borderRadius: 2,
					overflow: 'hidden'
				}}
			>
				<Stack spacing={1} sx={{ p: 2 }}>
					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<Typography
							variant="overline"
							sx={{
								color: 'rgba(255, 255, 255, 0.85)',
								lineHeight: 1.3,
								letterSpacing: '0.08em'
							}}
						>
							Free Guide
						</Typography>
						<IconButton
							aria-label="Dismiss"
							size="small"
							onClick={() => consolidatedPracticeId && dismissCapitalGuide(consolidatedPracticeId)}
							sx={{ color: 'rgba(255, 255, 255, 0.85)', p: 0.25, mr: -0.5 }}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Stack>
					<Typography
						variant="body2"
						sx={{
							color: '#fff',
							fontWeight: 600,
							lineHeight: 1.3
						}}
					>
						What will you do with more capital?
					</Typography>
					<Button
						variant="contained"
						size="small"
						endIcon={<ArrowForwardIcon />}
						onClick={handleOpen}
						sx={{
							alignSelf: 'flex-start',
							backgroundColor: '#fff',
							color: theme.palette.capital.primary,
							'&:hover': {
								backgroundColor: 'rgba(255, 255, 255, 0.9)'
							}
						}}
					>
						Read Now
					</Button>
				</Stack>
			</Card>
			<PdfViewerModal
				open={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				pdfUrl={pdfUrl}
				title={GUIDE_TITLE}
			/>
		</Grid>
	)
}

export default CapitalGuideBanner
